import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';

import { useAuthStore } from '../stores/useAuthStore';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import { VerificationForm } from '../components/auth/VerificationForm';
import { RequestPasswordResetForm } from '../components/auth/RequestPasswordResetForm';
import { ConfirmPasswordResetForm } from '../components/auth/ConfirmPasswordResetForm';
import { apiClient, getFullImageUrl } from '../lib/api';
import { ChevronRight, Search, UserRound } from 'lucide-react';
import logotk from "../media/cy_take_away.png"
import logo from "../media/cy-logo-negative.png"
import kieroLogo from "../media/Red_kiero.png"
import tacoIcon from "../media/taco.png"
import medialunaIcon from "../media/medialuna.png"
import chickenIcon from "../media/chicken.png"
import breadIcon from "../media/pan.png"

export function IdentificationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { validateSession, loginWithGoogle, isTakeAway, client, tenant } = useAuthStore();

  const [tenantUuid, setTenantUuid] = useState<string | null>(null);
  const [tenantLogotype, setTenantLogotype] = useState<string | null>(null);
  const [publicMenuEnabled, setPublicMenuEnabled] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  // Persist token on mount if present, or recover from storage
  React.useEffect(() => {
    const activeToken = token || sessionStorage.getItem('entry_token');
    if (activeToken) {
      if (token) {
        sessionStorage.setItem('entry_token', token);
      }
      fetchTenantConfig(activeToken);
    } else {
      setLoadingConfig(false);
      setView('login');
    }
  }, [token]);

  const fetchTenantConfig = async (qrToken: string) => {
    setLoadingConfig(true);
    try {
      // Limpiar caché y tokens previos para asegurar contexto limpio
      apiClient.setSessionToken(null);
      const { cacheService } = await import('../lib/cache');
      cacheService.clear();

      // Usar validateSession sin identifier para obtener info del tenant
      const response = await apiClient.validateSession(qrToken);
      if (response && response.tenant_uuid) {
        setTenantUuid(response.tenant_uuid);

        // Establecer el token obtenido para que getConfig() lo use en sus headers
        if (response.session_token) {
          apiClient.setSessionToken(response.session_token);
        }

        const configResp: any = await apiClient.getConfig();
        if (configResp?.success && configResp?.config) {
          const config = configResp.config;
          setTenantLogotype(config.logotype || null);

          // Using a more robust check for Ux field (handling case sensitivity and nulls)
          const features = config.plan?.features;
          const uxMode = (features?.Ux || (features as any)?.ux || '').toLowerCase();

          if (uxMode === 'simple') {
            // Flujo Foodtruck: ir directo al menú con carrito, sin login
            navigate(`/simple-menu/${qrToken}`, { replace: true });
            return;
          }

          if (uxMode === 'login') {
            // Solo login, sin opción de menú público
            setPublicMenuEnabled(false);
            setView('login');
          } else {
            // 'simplelogin' o sin campo: comportamiento original
            const mode = config.public_menu_mode;
            if (mode && mode !== 'disabled') {
              setPublicMenuEnabled(true);
              setView('choice');
            } else {
              setView('login');
            }
          }
        } else {
          setView('login');
        }
      } else {
        setView('login');
      }
    } catch (e) {
      console.error("Error fetching tenant config for public menu:", e);
      setView('login');
    } finally {
      setLoadingConfig(false);
    }
  };

  const [isValidatingGoogle, setIsValidatingGoogle] = useState(false);
  const [view, setView] = useState<'choice' | 'login' | 'register' | 'verify' | 'forgot-password' | 'reset-password'>('choice');
  const [resetEmail, setResetEmail] = useState('');

  const handleAuthCompletion = async (isGoogle = false) => {
    const currentState = useAuthStore.getState();
    const userClient = currentState.client;

    if (!isGoogle && userClient && userClient.is_verified === false) {
      setView('verify');
      return;
    }

    const activeToken = token || sessionStorage.getItem('entry_token');

    if (activeToken && userClient?.email) {
      try {
        const isValid = await validateSession(userClient.email, activeToken);
        if (isValid) {
          const state = useAuthStore.getState();
          const tableId = state.tableId || 'default';
          const { isTakeAway } = state;

          if (isTakeAway) {
            navigate(`/order-timing/${tableId}`, { replace: true });
          } else {
            useAuthStore.getState().setOrderTiming('today');
            useAuthStore.getState().setOrderMode(null);
            navigate(`/dashboard/${tableId}`, { replace: true });
          }
        }
      } catch (e) {
        console.error(e);
      }
    } else if (userClient?.is_verified) {
      const state = useAuthStore.getState();
      const tableId = state.tableId || 'default';
      navigate(`/dashboard/${tableId}`, { replace: true });
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      console.error("No credential received from Google");
      return;
    }

    setIsValidatingGoogle(true);
    try {
      await loginWithGoogle(credentialResponse.credential);
      await handleAuthCompletion(true);
    } catch (err) {
      console.error("Google Login Error", err);
    } finally {
      setIsValidatingGoogle(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google Login Failed');
  };

  // Determine which icons to show in orbit
  const isKiero = tenant?.partner_branding === 'KIERO';
  const showFoodIcons = !loadingConfig && !isKiero;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: 'var(--color-bg-page)' }}>
      <div className="rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-100 dark:border-gray-700" style={{ backgroundColor: 'var(--color-bg-card)' }}>

        {/* Header / Brand Area */}
        <div className="space-y-3 bg-gradient-to-r from-sky-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 p-8 text-center relative overflow-hidden">
          {/* Decorative Background Blobs */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-sky-200/40 rounded-full blur-3xl -translate-x-10 -translate-y-10"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-purple-200/40 rounded-full blur-3xl translate-x-10 translate-y-10"></div>

          <div className="relative z-10">
            {/* Logo Container with Irregular Shape */}
            <div className="relative mx-auto mb-6 w-40 h-40">
              {/* Orbit container for icons - only show if branding is confirmed as gastronomy */}
              {showFoodIcons && (
                <>
                  <div className="absolute inset-0 animate-orbit">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-4 w-10 h-10 opacity-80">
                      <img src={tacoIcon} alt="Taco" className="w-full h-full drop-shadow-lg" />
                    </div>
                  </div>

                  <div className="absolute inset-0 animate-orbit" style={{ animationDelay: '-5s' }}>
                    <div className="absolute top-1/2 right-0 translate-x-4 -translate-y-1/2 w-11 h-11 opacity-80">
                      <img src={chickenIcon} alt="Pollo" className="w-full h-full drop-shadow-lg" />
                    </div>
                  </div>

                  <div className="absolute inset-0 animate-orbit" style={{ animationDelay: '-10s' }}>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-4 w-12 h-9 opacity-80">
                      <img src={medialunaIcon} alt="Medialuna" className="w-full h-full drop-shadow-lg" />
                    </div>
                  </div>

                  <div className="absolute inset-0 animate-orbit" style={{ animationDelay: '-15s' }}>
                    <div className="absolute top-1/2 left-0 -translate-x-4 -translate-y-1/2 w-10 h-10 opacity-80">
                      <img src={breadIcon} alt="Pan" className="w-full h-full drop-shadow-lg" />
                    </div>
                  </div>
                </>
              )}

              {/* Main logo container with gradient border */}
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-sky-400 via-blue-500 to-purple-500 dark:from-sky-500 dark:via-blue-600 dark:to-purple-600 p-1 shadow-2xl">
                <div className="w-full h-full rounded-full bg-white dark:bg-gray-800 flex items-center justify-center overflow-hidden p-4">
                  {loadingConfig ? (
                    <img src={kieroLogo} alt="Loading..." className="w-full h-auto object-contain animate-pulse" />
                  ) : tenantLogotype ? (
                    <img src={getFullImageUrl(tenantLogotype)} alt="Logo" className="w-full h-full object-contain drop-shadow-lg" />
                  ) : isKiero ? (
                    <img src={kieroLogo} alt="Kiero" className="w-full h-auto object-contain" />
                  ) : isTakeAway ? (
                    <img src={logo} alt="Logo" className="w-full h-full object-contain drop-shadow-lg" />
                  ) : (
                    <img src={logotk} alt="Logo" className="w-full h-full object-contain drop-shadow-lg" />
                  )}
                </div>
              </div>
            </div>

            <h1 className="text-2xl pt-4 font-bold text-gray-900 dark:text-white mb-2">
              {loadingConfig ? "Preparando todo..." : "¡Te damos la bienvenida!"}
            </h1>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="p-8">
          {loadingConfig ? (
            <div className="py-20 flex flex-col items-center justify-center space-y-4">
              <LoadingSpinner />
              <p className="text-gray-400 text-sm font-medium animate-pulse">Cargando experiencia...</p>
            </div>
          ) : view === 'choice' ? (
            <div className="space-y-6 py-4">
              <button
                onClick={() => {
                  if (tenantUuid) {
                    navigate(`/public-menu/${tenantUuid}`);
                  }
                }}
                disabled={!publicMenuEnabled}
                className={`w-full group relative flex flex-col items-center justify-center p-6 rounded-[2rem] border transition-all duration-500 ${publicMenuEnabled
                  ? 'bg-gradient-to-br from-white/10 to-white/5 dark:from-gray-800/40 dark:to-gray-800/20 border-sky-500/20 hover:border-sky-500/40 backdrop-blur-xl shadow-lg shadow-sky-500/5 hover:shadow-sky-500/10'
                  : 'bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 opacity-50 cursor-not-allowed'
                  } hover:-translate-y-1`}
              >
                <div className={`w-14 h-14 rounded-[1.25rem] flex items-center justify-center mb-4 transition-all duration-500 ${publicMenuEnabled
                  ? 'bg-gradient-to-br from-sky-400 to-purple-500 text-white shadow-lg shadow-sky-500/30 group-hover:scale-110 group-hover:rotate-3'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                  }`}>
                  <Search className="w-7 h-7" />
                </div>
                <span className={`text-lg font-bold ${publicMenuEnabled ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                  Ver Menú
                </span>
                <p className="text-gray-500 dark:text-gray-400 text-xs font-medium mt-1">Explora nuestra carta</p>
                {publicMenuEnabled && (
                  <ChevronRight className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-sky-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                )}
              </button>

              <button
                onClick={() => setView('login')}
                className="w-full group relative flex flex-col items-center justify-center p-6 rounded-[2rem] border border-blue-500/20 bg-gradient-to-br from-white/10 to-white/5 dark:from-gray-800/40 dark:to-gray-800/20 backdrop-blur-xl shadow-lg shadow-blue-500/5 hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-[1.25rem] bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center mb-4 text-white shadow-lg shadow-blue-500/30 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-500">
                  <UserRound className="w-7 h-7" />
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white">Iniciar sesión</span>
                <p className="text-gray-500 dark:text-gray-400 text-xs font-medium mt-1">Para realizar tu pedido</p>
                <ChevronRight className="absolute right-6 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </button>

              {!publicMenuEnabled && (
                <p className="text-[10px] text-center text-gray-400 font-bold uppercase tracking-widest mt-4">
                  El menú público no está disponible para este comercio
                </p>
              )}
            </div>
          ) : view === 'verify' ? (
            <VerificationForm
              email={client?.email || ''}
              onSuccess={() => {
                setView('login');
                useAuthStore.getState().setError(null);
              }}
            />
          ) : view === 'forgot-password' ? (
            <RequestPasswordResetForm
              onSuccess={(email) => {
                setResetEmail(email);
                setView('reset-password');
                useAuthStore.getState().setError(null);
              }}
              onCancel={() => {
                setView('login');
                useAuthStore.getState().setError(null);
              }}
            />
          ) : view === 'reset-password' ? (
            <ConfirmPasswordResetForm
              email={resetEmail}
              onSuccess={() => {
                setView('login');
                useAuthStore.getState().setError(null);
              }}
              onCancel={() => {
                setView('login');
                useAuthStore.getState().setError(null);
              }}
            />
          ) : (
            <>
              {/* Back to Selection */}
              {publicMenuEnabled && (
                <button
                  onClick={() => setView('choice')}
                  className="flex items-center gap-2 text-gray-400 hover:text-sky-500 font-bold text-xs mb-6 uppercase tracking-widest transition-colors group"
                >
                  <ChevronRight className="w-4 h-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
                  Volver a opciones
                </button>
              )}

              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                <button
                  className={`flex-1 pb-4 text-sm font-medium transition-colors relative ${view === 'login'
                    ? 'text-sky-600 dark:text-sky-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  onClick={() => {
                    setView('login');
                    useAuthStore.getState().setError(null);
                  }}
                >
                  Iniciar sesión
                  {view === 'login' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400 rounded-t-full" />
                  )}
                </button>
                <button
                  className={`flex-1 pb-4 text-sm font-medium transition-colors relative ${view === 'register'
                    ? 'text-sky-600 dark:text-sky-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  onClick={() => {
                    setView('register');
                    useAuthStore.getState().setError(null);
                  }}
                >
                  Registrarse
                  {view === 'register' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400 rounded-t-full" />
                  )}
                </button>
              </div>

              {/* Forms */}
              <div className="min-h-[300px]">
                {view === 'login' ? (
                  <LoginForm
                    onSuccess={() => handleAuthCompletion(false)}
                    onForgotPassword={() => {
                      setView('forgot-password');
                      useAuthStore.getState().setError(null);
                    }}
                  />
                ) : (
                  <RegisterForm onSuccess={() => handleAuthCompletion(false)} />
                )}
              </div>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    O continuar con
                  </span>
                </div>
              </div>

              {/* Google Login */}
              <div className="flex flex-col items-center justify-center">
                {isValidatingGoogle ? (
                  <LoadingSpinner size="lg" message="Conectando con Google..." />
                ) : (
                  <div className="w-full">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      theme="filled_blue"
                      size="large"
                      shape="pill"
                      text="continue_with"
                      width="100%"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {view !== 'choice' && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Al continuar, aceptás los{' '}
              <button
                onClick={() => navigate(token ? `/${token}/tyc` : '/tyc')}
                className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 underline font-medium"
              >
                Términos y Condiciones
              </button>
              {' '}y la{' '}
              <button
                onClick={() => navigate(token ? `/${token}/privacy` : '/privacy')}
                className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 underline font-medium"
              >
                Política de Privacidad
              </button>
              .
            </p>
          </div>
        )}
      </div>
    </div>
  );
}